import { useEffect, useState } from "react";
import { downloadXlsx, stampedName } from "@/lib/exportXlsx";
import DownloadButton from "@/components/DownloadButton";

export interface PersonRow {
  id?: number;
  name: string;
  title?: string | null;
  email: string | null;
  phone: string | null;
  is_current?: boolean;
}

const MAX_PEOPLE = 10;

const emptyRow = (showCurrent: boolean): PersonRow => ({
  name: "",
  title: null,
  email: null,
  phone: null,
  ...(showCurrent ? { is_current: true } : {}),
});

interface Props {
  heading: string;
  people: PersonRow[];
  isLoading: boolean;
  onSave: (people: PersonRow[]) => Promise<void>;
  showTitle?: boolean;
  showCurrent?: boolean;
  displayLimit?: number;
  emptyText: string;
  addLabel: string;
  noteText?: string;
  exportBase?: string;
}

export default function PeopleEditor({
  heading,
  people,
  isLoading,
  onSave,
  showTitle = false,
  showCurrent = false,
  displayLimit,
  emptyText,
  addLabel,
  noteText,
  exportBase,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<PersonRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) setDraft(people.map((p) => ({ ...p })));
  }, [people, editing]);

  function startEdit() {
    setDraft(people.length ? people.map((p) => ({ ...p })) : [emptyRow(showCurrent)]);
    setMessage(null);
    setEditing(true);
  }

  function setField(i: number, k: keyof PersonRow, v: string | boolean) {
    setDraft((d) =>
      d.map((p, idx) =>
        idx === i ? { ...p, [k]: typeof v === "string" ? v || null : v } : p,
      ),
    );
  }

  async function save() {
    setSaving(true);
    setMessage(null);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (e) {
      setMessage(`Save failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  function exportXls() {
    const cols = [
      "Name",
      ...(showTitle ? ["Title"] : []),
      "Email",
      "Phone",
      ...(showCurrent ? ["Status"] : []),
    ];
    const rowsOut = people.map((p) => [
      p.name,
      ...(showTitle ? [p.title ?? ""] : []),
      p.email ?? "",
      p.phone ?? "",
      ...(showCurrent ? [p.is_current === false ? "Former" : "Current"] : []),
    ]);
    downloadXlsx(stampedName(exportBase ?? "people"), cols, rowsOut);
  }

  if (isLoading) {
    return <div className="card p-5 text-sm text-ink-500">Loading {heading.toLowerCase()}…</div>;
  }

  const shown = displayLimit ? people.slice(0, displayLimit) : people;

  return (
    <section className="card p-5">
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-medium text-ink-900">{heading}</h2>
        {!editing && (
          <div className="flex items-center gap-2">
            {exportBase && people.length > 0 && (
              <DownloadButton onClick={exportXls} label="Excel" />
            )}
            <button type="button" className="btn-ghost text-xs" onClick={startEdit}>
              {people.length ? "Edit" : addLabel}
            </button>
          </div>
        )}
      </div>

      {!editing ? (
        shown.length === 0 ? (
          <p className="text-sm text-ink-500">{emptyText}</p>
        ) : (
          <ul className="divide-y divide-ink-200">
            {shown.map((p, i) => (
              <li
                key={p.id ?? i}
                className={`flex items-start justify-between gap-4 py-3 first:pt-0 ${
                  showCurrent && p.is_current === false ? "opacity-60" : ""
                }`}
              >
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-ink-900">
                    {p.name}
                    {showCurrent && p.is_current === false && (
                      <span className="rounded-full bg-ink-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-ink-500">
                        Former
                      </span>
                    )}
                  </div>
                  {showTitle && p.title && <div className="text-xs text-ink-500">{p.title}</div>}
                </div>
                <div className="text-right text-xs">
                  {p.email && (
                    <div>
                      <a href={`mailto:${p.email}`} className="text-accent-600 hover:underline">
                        {p.email}
                      </a>
                    </div>
                  )}
                  {p.phone && (
                    <div>
                      <a href={`tel:${p.phone}`} className="text-ink-700 hover:underline">
                        {p.phone}
                      </a>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )
      ) : (
        <div className="space-y-3">
          {draft.map((p, i) => (
            <div key={i} className="grid grid-cols-1 gap-2 rounded-md border border-ink-200 p-3 sm:grid-cols-2">
              <input
                className="input"
                placeholder="Full name"
                value={p.name}
                onChange={(e) => setField(i, "name", e.target.value)}
              />
              {showTitle && (
                <input
                  className="input"
                  placeholder="Title / role"
                  value={p.title ?? ""}
                  onChange={(e) => setField(i, "title", e.target.value)}
                />
              )}
              <input
                className="input"
                placeholder="Email"
                value={p.email ?? ""}
                onChange={(e) => setField(i, "email", e.target.value)}
              />
              <input
                className="input"
                placeholder="Phone"
                value={p.phone ?? ""}
                onChange={(e) => setField(i, "phone", e.target.value)}
              />
              <div className="flex items-center justify-between sm:col-span-2">
                {showCurrent ? (
                  <label className="flex items-center gap-2 text-xs text-ink-700">
                    <input
                      type="checkbox"
                      checked={p.is_current ?? true}
                      onChange={(e) => setField(i, "is_current", e.target.checked)}
                    />
                    Currently on this account
                  </label>
                ) : (
                  <span />
                )}
                <button
                  type="button"
                  className="btn-ghost text-xs text-danger"
                  onClick={() => setDraft((d) => d.filter((_, idx) => idx !== i))}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              className="btn-ghost text-xs"
              disabled={draft.length >= MAX_PEOPLE}
              onClick={() => setDraft((d) => [...d, emptyRow(showCurrent)])}
            >
              + Add
            </button>
            <div className="flex items-center gap-3">
              {message && <span className="text-xs text-danger">{message}</span>}
              <button
                type="button"
                className="btn-ghost text-sm"
                onClick={() => setEditing(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button type="button" className="btn-primary text-sm" onClick={save} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
          {noteText && <p className="text-xs text-ink-500">{noteText}</p>}
        </div>
      )}
    </section>
  );
}
